import { useAtomValue } from 'jotai';
import { useEffect } from 'react';

import {
  PatientNoteUpdateSource,
  ProviderPatientNoteConnected,
  ProviderPatientNoteConnectedProps,
} from '@grow-therapy-team/seedling-components';

import { useTrackEvent } from '../../segment/segment';
import { EventTypeEnum } from '../../segment/types';
import { sessionInfoAtom } from '../../state';
import { inSessionPatientInformationAtom, selectedClientInfoAtom } from '../state';
import { useRecordScribbleEdited } from './useRecordScribbleEdited';

export function InSessionPatientNoteWrapper() {
  const { userViewed } = useTrackEvent();

  const patientInfo = useAtomValue(inSessionPatientInformationAtom);
  const patientShortId = patientInfo?.patientShortId ?? '';
  const sessionInfo = useAtomValue(sessionInfoAtom);
  const appointmentShortId = sessionInfo?.appointment?.shortId ?? '';

  const { onSaveNote } = useRecordScribbleEdited(patientShortId, true, appointmentShortId);

  useEffect(() => {
    if (!patientShortId || !appointmentShortId) return;
    userViewed(EventTypeEnum.SCRIBBLE_ACCESSED, {
      patientShortId,
      appointmentShortId,
      inSession: true,
    });
  }, [patientShortId, userViewed, appointmentShortId]);

  if (!patientShortId) {
    return null;
  }

  return (
    <ProviderPatientNoteConnected
      onSaveNote={onSaveNote}
      patientShortId={patientShortId}
      updateSource={PatientNoteUpdateSource.TELEHEALTH}
      variation='write-only'
      isInverse
    />
  );
}

type SelectedClientPatientNoteWrapperProps = {
  variation?: ProviderPatientNoteConnectedProps['variation'];
};

export function SelectedClientPatientNoteWrapper({ variation }: SelectedClientPatientNoteWrapperProps) {
  const { userViewed } = useTrackEvent();
  const patientInfo = useAtomValue(selectedClientInfoAtom);
  const patientShortId = patientInfo?.patientShortId ?? '';
  // The SelectedClientPatientNoteWrapper may be active during a session, or
  // outside of one. We check if there is an in-session client for the
  // corresponding patientShortId for this instance of the note and supply this
  // information as parameters for the Segment events we send.
  const inSessionClientInformation = useAtomValue(inSessionPatientInformationAtom);
  const appointmentShortId =
    inSessionClientInformation?.patientShortId === patientShortId
      ? inSessionClientInformation.inSessionAppointmentShortId
      : undefined;

  const { onSaveNote } = useRecordScribbleEdited(patientShortId, !!appointmentShortId, appointmentShortId);

  useEffect(() => {
    if (!patientShortId) return;

    userViewed(EventTypeEnum.SCRIBBLE_ACCESSED, {
      patientShortId,
      inSession: !!appointmentShortId,
      appointmentShortId,
    });
  }, [appointmentShortId, patientShortId, userViewed]);

  if (!patientShortId) {
    return null;
  }

  return (
    <ProviderPatientNoteConnected
      onSaveNote={onSaveNote}
      patientShortId={patientShortId}
      updateSource={PatientNoteUpdateSource.TELEHEALTH}
      variation={variation}
      isInverse
    />
  );
}
